import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Dossier, DossierSettlement, DossierSettlementVerdict, ROLE_LABEL } from '../../api/dossier';
import { BalanceBars } from '../Money/BalanceBars';
import { IdText } from '../Money/IdText';
import { FlowFailure, MoneyFlow } from '../Money/MoneyFlow';
import { clock } from '../Money/money';

const VERDICT: Record<DossierSettlementVerdict, { label: string; tone: string; icon: 'circle-check' | 'triangle-exclamation' | 'circle-question' }> = {
    InStep: { label: 'Loopt gelijk', tone: 'ok', icon: 'circle-check' },
    WasOutOfStep: { label: 'Liep uit elkaar', tone: 'warn', icon: 'triangle-exclamation' },
    OutOfStep: { label: 'Loopt uit elkaar', tone: 'warn', icon: 'triangle-exclamation' },
    NotMatchedYet: { label: 'Nog niet afgeletterd', tone: 'warn', icon: 'triangle-exclamation' },
    Unknown: { label: 'Niet te vergelijken', tone: 'muted', icon: 'circle-question' },
};

/** The connector's failures on the invoice and the objects under it, for the middle lane. */
const failuresOf = (dossier: Dossier, settlement: DossierSettlement): FlowFailure[] => {
    const nodes = new Map(dossier.nodes.map((node) => [node.key, node]));

    return dossier.moments
        .filter((moment) => moment.kind === 'Failed')
        .filter((moment) => moment.nodeKey === settlement.invoiceKey || nodes.get(moment.nodeKey)?.parentKey === settlement.invoiceKey)
        .map((moment) => {
            const node = nodes.get(moment.nodeKey);

            return {
                key: moment.id,
                at: moment.firstAt,
                lastAt: moment.lastAt,
                count: moment.count,
                title: moment.interpretation?.title ?? moment.headline,
                subject: node ? `${ROLE_LABEL[node.role] ?? node.role} ${node.relationId}` : moment.nodeKey,
            };
        });
};

/**
 * How each invoice in the dossier was settled: where Chargebee and Exact stand as bars on one scale, and
 * the money in three lanes with what the connector carried across and where it failed.
 */
export const SettlementView = ({ dossier }: { dossier: Dossier }) => (
    <section className="dossier__block" aria-label="Afrekening">
        <h4 className="dossier__heading">Geldstroom</h4>
        {(dossier.settlements ?? []).map((settlement) => (
            <SettlementCard key={settlement.invoiceKey} dossier={dossier} settlement={settlement} />
        ))}
    </section>
);

const SettlementCard = ({ dossier, settlement }: { dossier: Dossier; settlement: DossierSettlement }) => {
    const verdict = VERDICT[settlement.verdict] ?? VERDICT.Unknown;

    return (
        <article className="settlement">
            <header className="settlement__head">
                <span className="settlement__title">Factuur {settlement.invoiceId}</span>
                <span className={`dossier__state dossier__state--${verdict.tone}`}>
                    <FontAwesomeIcon icon={verdict.icon} /> {verdict.label}
                </span>
                {settlement.readAt && <span className="settlement__read">live gelezen om {clock(settlement.readAt)}</span>}
            </header>

            <BalanceBars settlement={settlement} hideVerdict={settlement.findings.length > 0} />

            {settlement.findings.length > 0 && (
                <ul className="settlement__findings">
                    {settlement.findings.map((finding) => (
                        <li key={finding.text} className={finding.warning ? 'settlement__finding settlement__finding--warning' : 'settlement__finding'}>
                            <FontAwesomeIcon icon={finding.warning ? 'triangle-exclamation' : 'circle-info'} />
                            <span>
                                <IdText text={finding.text} />
                            </span>
                        </li>
                    ))}
                </ul>
            )}

            {settlement.events.length > 0 && <MoneyFlow settlement={settlement} failures={failuresOf(dossier, settlement)} highlight={dossier.rootId} />}
        </article>
    );
};
